import React from 'react';
import clsx from 'clsx';
import { Calendar, Filter, RefreshCw, Building2 } from 'lucide-react';
import type { DateRange } from '../../hooks/useDateRangePresets';

export interface ReportsFiltersProps {
    dateRange: DateRange;
    compareToRange: DateRange;
    selectedPreset: string;
    selectedComparePreset: string;
    selectedYear: number;
    selectedCompareYear: number;
    onPresetChange: (preset: string) => void;
    onComparePresetChange: (preset: string) => void;
    onYearChange: (year: number) => void;
    onCompareYearChange: (year: number) => void;
    onDateRangeChange: (field: 'startDay' | 'endDay', value: string) => void;
    onCompareToRangeChange: (field: 'startDay' | 'endDay', value: string) => void;
    onApplyFilter: () => void;
    onRefresh: () => void;
    isApplying: boolean;
    disabled?: boolean;
    // Tenant filter props
    providers?: string[];
    tenants?: string[];
    selectedProvider?: string;
    selectedTenant?: string;
    isGlobalAdmin?: boolean;
    onProviderChange?: (provider: string) => void;
    onTenantChange?: (tenant: string) => void;
}

/**
 * Compact filter controls for the Reports page
 * Follows the professional design pattern with inline layout and brand styling
 */
export const ReportsFilters: React.FC<ReportsFiltersProps> = ({
    dateRange,
    compareToRange,
    selectedPreset,
    selectedComparePreset,
    selectedYear,
    selectedCompareYear,
    onPresetChange,
    onComparePresetChange,
    onYearChange,
    onCompareYearChange,
    onDateRangeChange,
    onCompareToRangeChange,
    onApplyFilter,
    onRefresh,
    isApplying,
    disabled = false,
    providers = [],
    tenants = [],
    selectedProvider = '',
    selectedTenant = '',
    isGlobalAdmin = false,
    onProviderChange,
    onTenantChange
}) => {
    const currentYear = new Date().getFullYear();

    // Generate year options (5 previous years + current year)
    const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);

    return (
        <section className="mb-8 rounded-card border border-rm-neutral-600/20 bg-white p-6 shadow-soft">
            {isApplying && (
                <div className="mb-4 flex items-center gap-3 rounded-card border border-rm-accent-300/30 bg-rm-accent-300/10 p-3 text-rm-primary-700">
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    <span className="text-sm font-medium">Applying preset filter and refreshing data...</span>
                </div>
            )}

            <div className="space-y-6">
                {/* Provider/Tenant Filters - visible when tenant data is available */}
                {(providers.length > 0 || tenants.length > 1) && (
                    <div className="flex flex-wrap items-center gap-4">
                        <div className="inline-flex items-center gap-2 rounded-pill bg-rm-primary-700/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-rm-primary-700">
                            <Building2 className="h-3.5 w-3.5" />
                            SCOPE
                        </div>

                        {/* Provider Dropdown */}
                        <div className="flex items-center gap-2">
                            <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">Provider</label>
                            <select
                                value={selectedProvider}
                                onChange={(e) => onProviderChange?.(e.target.value)}
                                disabled={isApplying || disabled}
                                className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {isGlobalAdmin && <option value="">All providers</option>}
                                {providers.map(p => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>

                        {/* Tenant Dropdown - hidden if only one tenant */}
                        {tenants.length > 1 && (
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">Tenant</label>
                                <select
                                    value={selectedTenant}
                                    onChange={(e) => onTenantChange?.(e.target.value)}
                                    disabled={isApplying || disabled}
                                    className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {isGlobalAdmin && <option value="">All tenants</option>}
                                    {tenants.map(t => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                )}

                {/* Primary Period Filters */}
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-wrap items-center gap-4">
                            <div className="inline-flex items-center gap-2 rounded-pill bg-rm-accent-300/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-rm-primary-700">
                                <Filter className="h-3.5 w-3.5" />
                                CURRENT REPORT
                            </div>

                            {/* Preset Dropdown */}
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">Period</label>
                                <select
                                    value={selectedPreset}
                                    onChange={(e) => onPresetChange(e.target.value)}
                                    disabled={isApplying || disabled}
                                    className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <option value="custom">Custom dates</option>
                                    <option value="lastWeek">Last week</option>
                                    <option value="last2Weeks">Last 2 weeks</option>
                                    <option value="last30Days">Last 30 days</option>
                                    <option value="lastMonth">Last month</option>
                                    <option value="q1">Q1 [Jan-Mar]</option>
                                    <option value="q2">Q2 [Apr-Jun]</option>
                                    <option value="q3">Q3 [Jul-Sep]</option>
                                    <option value="q4">Q4 [Oct-Dec]</option>
                                    <option value="1h">1H [Jan-Jun]</option>
                                    <option value="2h">2H [Jul-Dec]</option>
                                    <option value="cy">CY [Jan-Dec]</option>
                                </select>
                            </div>

                            {/* Year Dropdown - show for quarterly/half-yearly/yearly periods */}
                            {['q1', 'q2', 'q3', 'q4', '1h', '2h', 'cy'].includes(selectedPreset) && (
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">Year</label>
                                    <select
                                        value={selectedYear}
                                        onChange={(e) => onYearChange(Number(e.target.value))}
                                        disabled={isApplying || disabled}
                                        className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {yearOptions.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Manual Date Inputs */}
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">From</label>
                                <input
                                    type="date"
                                    lang="en-US"
                                    value={dateRange.startDay}
                                    max={dateRange.endDay}
                                    onChange={(e) => onDateRangeChange('startDay', e.target.value)}
                                    disabled={disabled || isApplying}
                                    className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">To</label>
                                <input
                                    type="date"
                                    lang="en-US"
                                    value={dateRange.endDay}
                                    min={dateRange.startDay}
                                    onChange={(e) => onDateRangeChange('endDay', e.target.value)}
                                    disabled={disabled || isApplying}
                                    className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        <button
                            onClick={onApplyFilter}
                            disabled={disabled || selectedPreset !== 'custom' || isApplying}
                            className={clsx(
                                'flex items-center gap-2 rounded-card px-4 py-2 text-sm font-semibold shadow-sm transition disabled:cursor-not-allowed disabled:opacity-60',
                                selectedPreset === 'custom'
                                    ? 'bg-rm-cta text-rm-primary-700 hover:opacity-90'
                                    : 'bg-rm-neutral-600/10 text-rm-neutral-600'
                            )}
                            title={selectedPreset !== 'custom' ? 'Preset filters are applied automatically' : 'Apply your custom date ranges (primary and comparison periods)'}
                        >
                            <Calendar className="h-4 w-4" />
                            {selectedPreset === 'custom' ? 'Apply custom dates' : 'Applied'}
                        </button>
                        <button
                            onClick={onRefresh}
                            disabled={disabled || isApplying}
                            className="flex items-center gap-2 rounded-card bg-rm-bg-50 px-4 py-2 text-sm font-semibold text-rm-ink-900 shadow-sm transition hover:bg-rm-neutral-600/10 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            <RefreshCw className={clsx('h-4 w-4', { 'animate-spin': isApplying })} />
                        </button>
                    </div>
                </div>

                {/* Compare To Period - Always Expanded */}
                <div className="border-t border-rm-neutral-600/20 pt-6">
                    <div className="flex flex-wrap items-center gap-3">
                        {/* Label Badge */}
                        <div className="inline-flex items-center gap-2 rounded-pill bg-rm-teal-600/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-rm-teal-600">
                            <Calendar className="h-3.5 w-3.5" />
                            Compare To
                        </div>

                        {/* Full Controls */}
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Compare Period Dropdown */}
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">Period</label>
                                <select
                                    value={selectedComparePreset}
                                    onChange={(e) => onComparePresetChange(e.target.value)}
                                    disabled={isApplying || disabled}
                                    className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    <option value="custom">Custom dates</option>
                                    <option value="previousWeek">Previous week</option>
                                    <option value="previous2Weeks">Previous 2 weeks</option>
                                    <option value="previous30Days">Previous 30 days</option>
                                    <option value="previousMonth">Previous month</option>
                                    <option value="q1">Q1 [Jan-Mar]</option>
                                    <option value="q2">Q2 [Apr-Jun]</option>
                                    <option value="q3">Q3 [Jul-Sep]</option>
                                    <option value="q4">Q4 [Oct-Dec]</option>
                                    <option value="1h">1H [Jan-Jun]</option>
                                    <option value="2h">2H [Jul-Dec]</option>
                                    <option value="cy">CY [Jan-Dec]</option>
                                </select>
                            </div>

                            {/* Compare Year Dropdown - show for quarterly/half-yearly/yearly periods */}
                            {['q1', 'q2', 'q3', 'q4', '1h', '2h', 'cy'].includes(selectedComparePreset) && (
                                <div className="flex items-center gap-2">
                                    <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">Year</label>
                                    <select
                                        value={selectedCompareYear}
                                        onChange={(e) => onCompareYearChange(Number(e.target.value))}
                                        disabled={isApplying || disabled}
                                        className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                                    >
                                        {yearOptions.map(year => (
                                            <option key={year} value={year}>{year}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Manual Date Inputs */}
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">From</label>
                                <input
                                    type="date"
                                    lang="en-US"
                                    value={compareToRange.startDay}
                                    max={compareToRange.endDay}
                                    onChange={(e) => onCompareToRangeChange('startDay', e.target.value)}
                                    disabled={disabled || isApplying}
                                    className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-semibold uppercase tracking-wide text-rm-neutral-600">To</label>
                                <input
                                    type="date"
                                    lang="en-US"
                                    value={compareToRange.endDay}
                                    min={compareToRange.startDay}
                                    onChange={(e) => onCompareToRangeChange('endDay', e.target.value)}
                                    disabled={disabled || isApplying}
                                    className="rounded-card border border-rm-neutral-600/30 bg-white px-3 py-2 text-sm text-rm-ink-900 shadow-sm transition focus:border-rm-accent-500 focus:outline-none focus:ring-2 focus:ring-rm-accent-500/40 disabled:cursor-not-allowed disabled:opacity-60"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};
